import { apiClient } from './apiClient';

import type { ApiResponse } from '@/types/auth.types';
import type {
  FunnelReport,
  NotificationsReport,
  ReportFilters,
  SchedulingReport,
  SlaReport,
} from '@/types/report.types';

function buildQuery(filters?: ReportFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.roles) params.set('roles', filters.roles);
  if (filters.sites) params.set('sites', filters.sites);
  if (filters.jobPostingId) params.set('jobPostingId', filters.jobPostingId);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getFunnelReport(filters?: ReportFilters): Promise<FunnelReport> {
  const response = await apiClient.get<ApiResponse<FunnelReport>>(`/reports/funnel${buildQuery(filters)}`);
  return response.data;
}

export async function getSlaReport(filters?: ReportFilters): Promise<SlaReport> {
  const response = await apiClient.get<ApiResponse<SlaReport>>(`/reports/sla${buildQuery(filters)}`);
  return response.data;
}

export async function getSchedulingReport(filters?: ReportFilters): Promise<SchedulingReport> {
  const response = await apiClient.get<ApiResponse<SchedulingReport>>(`/reports/scheduling${buildQuery(filters)}`);
  return response.data;
}

export async function getNotificationsReport(filters?: ReportFilters): Promise<NotificationsReport> {
  const response = await apiClient.get<ApiResponse<NotificationsReport>>(
    `/reports/notifications${buildQuery(filters)}`,
  );
  return response.data;
}

export async function exportReport(
  reportType: 'funnel' | 'sla' | 'scheduling' | 'notifications',
  filters?: ReportFilters,
): Promise<void> {
  const query = buildQuery(filters);
  const separator = query ? '&' : '?';
  const url = `/reports/export${query}${separator}report=${reportType}`;
  const token = localStorage.getItem('kent-hr-auth');
  const parsed = token ? (JSON.parse(token) as { state?: { accessToken?: string } }) : null;
  const authHeader = parsed?.state?.accessToken ? `Bearer ${parsed.state.accessToken}` : '';

  const response = await fetch(`/api${url}`, {
    headers: { Authorization: authHeader },
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  const text = await response.text();
  const blob = new Blob([text], { type: 'text/csv' });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `${reportType}-report.csv`;
  link.click();
  URL.revokeObjectURL(downloadUrl);
}
